'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { Link } from '@algoacademy/design-system/web/actions/Link';
import { showErrorToastWithContactFormLink } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { ErrorMessage, Field, FieldGroup, Fieldset, Label } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input } from '@algoacademy/design-system/web/forms/Input';
import { Textarea } from '@algoacademy/design-system/web/forms/Textarea';
import { GithubLogo } from '@algoacademy/design-system/web/marketing/logos/socials/GithubLogo';
import { LinkedinLogo } from '@algoacademy/design-system/web/marketing/logos/socials/LinkedinLogo';
import { YoutubeLogo } from '@algoacademy/design-system/web/marketing/logos/socials/YoutubeLogo';
import { H2 } from '@algoacademy/design-system/web/typography/Heading';
import { Label1 } from '@algoacademy/design-system/web/typography/Label';
import { EnvelopeIcon } from '@heroicons/react/24/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { cn } from '@/app/_lib/utils';
import { useTRPC } from '@/app/_trpc/config';
import { buildContactFormEmail } from '@/app/(marketing-site)/_helpers/email-templates';
import { type ContactSchema, ContactSchemaZ } from './schema';
type ContactFormProps = {
  email: string;
  githubUrl: string;
  linkedinUrl: string;
  youtubeUrl: string;
};
export function ContactForm(t0) {
  const $ = _c(89);
  const {
    email,
    githubUrl,
    linkedinUrl,
    youtubeUrl
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      resolver: zodResolver(ContactSchemaZ),
      defaultValues: {
        fullName: "",
        topic: "",
        email: "",
        content: ""
      },
      mode: "onSubmit"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    register,
    handleSubmit,
    formState: t2
  } = useForm(t1);
  const {
    errors
  } = t2;
  const trpc = useTRPC();
  let t3;
  if ($[1] !== trpc.notifications.sendEmails) {
    t3 = trpc.notifications.sendEmails.mutationOptions();
    $[1] = trpc.notifications.sendEmails;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const sendEmailsMutation = useMutation(t3);
  let t4;
  if ($[3] !== sendEmailsMutation) {
    t4 = async data => {
      try {
        await sendEmailsMutation.mutateAsync({
          emailData: buildContactFormEmail(data)
        });
        toast.success("Wiadomo\u015B\u0107 wys\u0142ana!", {
          description: "Dzi\u0119kujemy za Twoje pytanie. Odpowied\u017A otrzymasz w ci\u0105gu 3 dni roboczych."
        });
      } catch {
        showErrorToastWithContactFormLink();
      }
    };
    $[3] = sendEmailsMutation;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const onSubmit = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <H2>Skontaktuj się ze mną</H2>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <EnvelopeIcon className="h-5 w-5" />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== email) {
    t7 = <Label1 className="flex items-center gap-2 text-algoprimary">{t6}{email}</Label1>;
    $[7] = email;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== githubUrl) {
    t8 = githubUrl && <Link href={githubUrl} target="_blank"><GithubLogo className="size-9 rounded-full border bg-white py-1.5 text-algoprimary" /></Link>;
    $[9] = githubUrl;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== linkedinUrl) {
    t9 = linkedinUrl && <Link href={linkedinUrl} target="_blank"><LinkedinLogo className="size-9 rounded-full border bg-white py-1.5 text-algoprimary" /></Link>;
    $[11] = linkedinUrl;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== youtubeUrl) {
    t10 = youtubeUrl && <Link href={youtubeUrl} target="_blank"><YoutubeLogo className="size-9 rounded-full border bg-white py-1.5 text-algoprimary" /></Link>;
    $[13] = youtubeUrl;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t8 || $[17] !== t9) {
    t11 = <div className="inline-flex space-x-6">{t8}{t9}{t10}</div>;
    $[15] = t10;
    $[16] = t8;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t7) {
    t12 = <div className="flex flex-col items-center space-y-4">{t5}<div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-8">{t7}{t11}</div></div>;
    $[19] = t11;
    $[20] = t7;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== handleSubmit || $[23] !== onSubmit) {
    t13 = handleSubmit(onSubmit);
    $[22] = handleSubmit;
    $[23] = onSubmit;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Label>Imię i nazwisko *</Label>;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== register) {
    t15 = register("fullName");
    $[26] = register;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15) {
    t16 = <Input {...t15} name="fullName" type="text" />;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = !errors.fullName?.message && "invisible";
  let t18;
  if ($[30] !== t17) {
    t18 = cn(t17);
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = errors.fullName?.message ?? " ";
  let t20;
  if ($[32] !== t18 || $[33] !== t19) {
    t20 = <ErrorMessage className={t18}>{t19}</ErrorMessage>;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t16 || $[36] !== t20) {
    t21 = <Field>{t14}{t16}{t20}</Field>;
    $[35] = t16;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Label>Email *</Label>;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== register) {
    t23 = register("email");
    $[39] = register;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t23) {
    t24 = <Input {...t23} name="email" type="email" />;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const t25 = !errors.email?.message && "invisible";
  let t26;
  if ($[43] !== t25) {
    t26 = cn(t25);
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  const t27 = errors.email?.message ?? " ";
  let t28;
  if ($[45] !== t26 || $[46] !== t27) {
    t28 = <ErrorMessage className={t26}>{t27}</ErrorMessage>;
    $[45] = t26;
    $[46] = t27;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== t24 || $[49] !== t28) {
    t29 = <Field>{t22}{t24}{t28}</Field>;
    $[48] = t24;
    $[49] = t28;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Label>Temat wiadomości *</Label>;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== register) {
    t31 = register("topic");
    $[52] = register;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== t31) {
    t32 = <Input {...t31} name="topic" type="text" />;
    $[54] = t31;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  const t33 = !errors.topic?.message && "invisible";
  let t34;
  if ($[56] !== t33) {
    t34 = cn(t33);
    $[56] = t33;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  const t35 = errors.topic?.message ?? " ";
  let t36;
  if ($[58] !== t34 || $[59] !== t35) {
    t36 = <ErrorMessage className={t34}>{t35}</ErrorMessage>;
    $[58] = t34;
    $[59] = t35;
    $[60] = t36;
  } else {
    t36 = $[60];
  }
  let t37;
  if ($[61] !== t32 || $[62] !== t36) {
    t37 = <Field>{t30}{t32}{t36}</Field>;
    $[61] = t32;
    $[62] = t36;
    $[63] = t37;
  } else {
    t37 = $[63];
  }
  let t38;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Label>Wiadomość *</Label>;
    $[64] = t38;
  } else {
    t38 = $[64];
  }
  let t39;
  if ($[65] !== register) {
    t39 = register("content");
    $[65] = register;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  let t40;
  if ($[67] !== t39) {
    t40 = <Textarea {...t39} name="content" rows={6} />;
    $[67] = t39;
    $[68] = t40;
  } else {
    t40 = $[68];
  }
  const t41 = !errors.content?.message && "invisible";
  let t42;
  if ($[69] !== t41) {
    t42 = cn(t41);
    $[69] = t41;
    $[70] = t42;
  } else {
    t42 = $[70];
  }
  const t43 = errors.content?.message ?? " ";
  let t44;
  if ($[71] !== t42 || $[72] !== t43) {
    t44 = <ErrorMessage className={t42}>{t43}</ErrorMessage>;
    $[71] = t42;
    $[72] = t43;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  let t45;
  if ($[74] !== t40 || $[75] !== t44) {
    t45 = <Field>{t38}{t40}{t44}</Field>;
    $[74] = t40;
    $[75] = t44;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  let t46;
  if ($[77] !== t21 || $[78] !== t29 || $[79] !== t37 || $[80] !== t45) {
    t46 = <Fieldset><FieldGroup className="space-y-6">{t21}{t29}{t37}{t45}</FieldGroup></Fieldset>;
    $[77] = t21;
    $[78] = t29;
    $[79] = t37;
    $[80] = t45;
    $[81] = t46;
  } else {
    t46 = $[81];
  }
  let t47;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Button type="submit" className="self-start">Wyślij wiadomość</Button>;
    $[82] = t47;
  } else {
    t47 = $[82];
  }
  let t48;
  if ($[83] !== t13 || $[84] !== t46) {
    t48 = <div><form onSubmit={t13} className="space-y-4">{t46}{t47}</form></div>;
    $[83] = t13;
    $[84] = t46;
    $[85] = t48;
  } else {
    t48 = $[85];
  }
  let t49;
  if ($[86] !== t12 || $[87] !== t48) {
    t49 = <div id="contact-form" className="flex flex-col space-y-6 sm:w-xl md:w-2xl lg:w-3xl">{t12}{t48}</div>;
    $[86] = t12;
    $[87] = t48;
    $[88] = t49;
  } else {
    t49 = $[88];
  }
  return t49;
}